import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSuccess, showError } from '../utils/alerts';
import Modal from './Modal';
import { CustomerFormData } from '../types/customer';
import { customerService } from '../services/customerService';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer?: any; // For edit mode
}

const validationSchema = yup.object({
  type: yup.string().oneOf(['NATURAL', 'JURIDICA']).required('Tipo de cliente es requerido'),
  name: yup.string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(255, 'El nombre no puede exceder 255 caracteres')
    .required('Nombre es requerido'),
  doc_number: yup.string()
    .max(50, 'El número de documento no puede exceder 50 caracteres')
    .required('Número de documento es requerido'),
  email: yup.string()
    .email('Ingrese un email válido')
    .default(''),
  phone: yup.string()
    .max(20, 'El teléfono no puede exceder 20 caracteres')
    .default(''),
  address: yup.string().default(''),
  notes: yup.string().default(''),
  active: yup.boolean().default(true)
});

const AddClientModal: React.FC<AddClientModalProps> = ({ isOpen, onClose, customer }) => {
  const queryClient = useQueryClient();
  const isEditMode = !!customer;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<CustomerFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type: customer?.type || 'NATURAL',
      name: customer?.name || '',
      doc_number: customer?.doc_number || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
      address: customer?.address || '',
      notes: customer?.notes || '',
      active: customer?.active ?? true
    }
  });

  // Reset form when customer changes (for edit mode)
  useEffect(() => {
    if (customer) {
      reset({
        type: customer.type || 'NATURAL',
        name: customer.name || '',
        doc_number: customer.doc_number || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
        active: customer.active ?? true
      });
    }
  }, [customer, reset]);

  const createCustomerMutation = useMutation({
    mutationFn: customerService.createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showSuccess('Cliente creado exitosamente');
      reset();
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al crear el cliente';
      showError(message);
    }
  });

  const updateCustomerMutation = useMutation({
    mutationFn: (data: { id: number; customerData: Partial<CustomerFormData> }) =>
      customerService.updateCustomer(data.id, data.customerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showSuccess('Cliente actualizado exitosamente');
      reset();
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al actualizar el cliente';
      showError(message);
    }
  });

  const onSubmit = async (data: CustomerFormData) => {
    try {
      if (isEditMode && customer) {
        await updateCustomerMutation.mutateAsync({
          id: customer.id,
          customerData: data
        });
      } else {
        await createCustomerMutation.mutateAsync(data);
      }
    } catch (error) {
      // Error is handled in the mutation onError callback
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEditMode ? "Editar Cliente" : "Agregar Cliente"} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Tipo de Cliente */}
          <div>
            <label className="form-label">
              Tipo de Cliente <span className="text-red-500">*</span>
            </label>
            <select {...register('type')} className="form-select">
              <option value="NATURAL">Persona Natural</option>
              <option value="JURIDICA">Persona Jurídica</option>
            </select>
            {errors.type && (
              <p className="form-error">{errors.type.message}</p>
            )}
          </div>

          {/* Número de Documento */}
          <div>
            <label className="form-label">Número de Documento</label>
            <input
              type="text"
              {...register('doc_number')}
              className="form-input"
              placeholder="DPI, Pasaporte, NIT..."
            />
            {errors.doc_number && (
              <p className="form-error">{errors.doc_number.message}</p>
            )}
          </div>
        </div>

        {/* Nombre */}
        <div>
          <label className="form-label">
            Nombre Completo <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('name')}
            className="form-input"
            placeholder="Nombre completo del cliente"
          />
          {errors.name && (
            <p className="form-error">{errors.name.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Email */}
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              {...register('email')}
              className="form-input"
              placeholder="correo@ejemplo.com"
            />
            {errors.email && (
              <p className="form-error">{errors.email.message}</p>
            )}
          </div>

          {/* Teléfono */}
          <div>
            <label className="form-label">Teléfono</label>
            <input
              type="tel"
              {...register('phone')}
              className="form-input"
              placeholder="+507 6000-0000"
            />
            {errors.phone && (
              <p className="form-error">{errors.phone.message}</p>
            )}
          </div>
        </div>

        {/* Dirección */}
        <div>
          <label className="form-label">Dirección</label>
          <textarea
            {...register('address')}
            rows={3}
            className="form-textarea"
            placeholder="Dirección completa del cliente"
          />
          {errors.address && (
            <p className="form-error">{errors.address.message}</p>
          )}
        </div>

        {/* Notas */}
        <div>
          <label className="form-label">Notas</label>
          <textarea
            {...register('notes')}
            rows={3}
            className="form-textarea"
            placeholder="Notas adicionales sobre el cliente"
          />
          {errors.notes && (
            <p className="form-error">{errors.notes.message}</p>
          )}
        </div>

        {/* Botones */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary order-2 sm:order-1"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary order-1 sm:order-2"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (isEditMode ? 'Actualizando...' : 'Creando...')
              : (isEditMode ? 'Actualizar Cliente' : 'Crear Cliente')
            }
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddClientModal;